import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { CheckpointDetailCard } from "@/presentation/components/timeline-v2/checkpoint-detail-card";
import { HorizontalTrack } from "@/presentation/components/timeline-v2/horizontal-track";
import type { MilestoneKey } from "@/presentation/components/timeline-v2/horizontal-track";
import { PageHeader } from "@/presentation/components/shell/page-header";
import { WeekDetailCard } from "@/presentation/components/timeline-v2/week-detail-card";

import { getCurrentWeekNumber, timelineV2 } from "./-timeline-v2.data";
import type { CheckpointEntry, TimelineEntry, WeekEntry } from "./-timeline-v2.data";

export const Route = createFileRoute("/_app/timeline-v2")({
  component: TimelineV2Page,
});

function TimelineV2Page() {
  const currentWeek = getCurrentWeekNumber();
  const [selected, setSelected] = useState<MilestoneKey>(currentWeek ?? 1);
  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-5xl flex-col">
      <PageHeader
        eyebrow="Curated narrative · post-checkpoint"
        title="Timeline 2.0"
        summary="Milestones along the May 2026 axis — the owner checkpoint on May 13 marks the inflection between discovery and build-spec. Click a node to view its detail."
      />
      <PageBody
        entries={timelineV2}
        selected={selected}
        currentWeek={currentWeek}
        onSelect={setSelected}
      />
    </div>
  );
}

interface PageBodyProps {
  entries: TimelineEntry[];
  selected: MilestoneKey;
  currentWeek: 1 | 2 | 3 | 4 | null;
  onSelect: (key: MilestoneKey) => void;
}

function PageBody({ entries, selected, currentWeek, onSelect }: PageBodyProps) {
  const detailEntry = findEntry(entries, selected);
  return (
    <div className="flex flex-col gap-6 px-6 py-8 lg:px-12">
      <div className="print:hidden">
        <HorizontalTrack
          entries={entries}
          selected={selected}
          currentWeek={currentWeek}
          onSelect={onSelect}
        />
      </div>
      <Detail entry={detailEntry} />
    </div>
  );
}

function Detail({ entry }: { entry: TimelineEntry | null }) {
  if (!entry) return null;
  if (entry.kind === "checkpoint") {
    return <CheckpointDetailCard entry={entry} />;
  }
  return <WeekDetailCard entry={entry} />;
}

function findEntry(entries: TimelineEntry[], key: MilestoneKey): TimelineEntry | null {
  if (typeof key === "string") {
    return (
      (entries.find((e) => e.kind === "checkpoint" && e.id === key) as
        | CheckpointEntry
        | undefined) ?? null
    );
  }
  return (
    (entries.find((e) => e.kind === "week" && e.number === key) as WeekEntry | undefined) ?? null
  );
}
